import React, { useMemo } from 'react';

import Suspense from '@atlaskit/react-ufo/suspense';
import { History } from 'history';

import { checkUserHasCapabilitiesFromVolt } from '@townsquare/capability-provider/checkUserHasCapabilities';
import { type CollaborationContext } from '@townsquare/collab-context';
import { filterFalsyStrings, filterNull } from '@townsquare/filter-type';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { User } from '@townsquare/user-store/types';
import { useWorkspaceActions } from '@townsquare/workspace-store/actions';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';

import { BootstrapShell, type BootstrapShellData } from './BootstrapShell';
import { BootstrapErrorBoundary } from './ErrorBoundary/BootstrapErrorBoundary';
import { NotFoundScreen } from './ErrorBoundary/scenarios';
import { deriveContextCloudId } from './deriveBootstrap';
import { BootstrapVoltPayload, BootstrapVoltWorkspace } from './voltPayload';

/**
 * Render-prop output of `BootstrapFromVolt`. Same shape as `BootstrapResult`
 * from `Bootstrap.tsx` except `workspaceVolt` is plain data, not a Relay
 * fragment ref.
 */
export type BootstrapFromVoltResult = {
	orgId?: string;
	cloudIds: string[];
	workspaceIds: string[];
	workspaceUuids: string[];
	workspace?: WorkspaceDetails;
	user: User;
	workspaceVolt: BootstrapVoltWorkspace | undefined;
	collabContext?: CollaborationContext | null;
};

type URLSearch = {
	[k: string]: string;
};

type BootstrapFromVoltProps = {
	children: (data: BootstrapFromVoltResult) => JSX.Element;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	payload: BootstrapVoltPayload;
	urlSearch: URLSearch;
};

function BootstrapFromVoltResolution(props: BootstrapFromVoltProps): JSX.Element {
	const bootstrap = props.payload;

	const orgId = bootstrap.orgId ?? undefined;
	// Memoised so referential equality holds across renders — the shell uses
	// these as `useMemo` deps and the inlined window global is itself stable.
	// Master had `orgCloudIds: ... ?? null`; preserve that exact semantic.
	const orgCloudIds = useMemo(
		() => bootstrap.cloudIds?.filter(filterFalsyStrings) ?? null,
		[bootstrap.cloudIds],
	);
	const cloudIds = useMemo(() => orgCloudIds ?? [], [orgCloudIds]);
	const workspaces = useMemo(
		() => bootstrap.workspaces?.filter(filterNull) ?? [],
		[bootstrap.workspaces],
	);
	const workspaceIds = useMemo(
		() => workspaces.map((w) => w.id).filter(filterFalsyStrings),
		[workspaces],
	);
	const workspaceUuids = useMemo(
		() => workspaces.map((w) => w.uuid).filter(filterFalsyStrings),
		[workspaces],
	);
	const collabContextCloudIds = useMemo(
		() => bootstrap.collabContext?.cloudIds?.filter(filterFalsyStrings) ?? [],
		[bootstrap.collabContext?.cloudIds],
	);
	const collabContextSiteAris = useMemo(
		() => bootstrap.collabContext?.collabContextSiteAris?.filter(filterNull) ?? [],
		[bootstrap.collabContext?.collabContextSiteAris],
	);

	const contextCloudId = useMemo(
		() =>
			deriveContextCloudId(
				props.matchedRoute?.match?.params.siteIdentifier ?? props.urlSearch.cloudId ?? undefined,
				cloudIds,
			),
		[cloudIds, props.matchedRoute?.match?.params.siteIdentifier, props.urlSearch.cloudId],
	);
	const workspace = useMemo(
		() =>
			workspaces
				// Exclude PRODUCT workspaces, treat them as no workspace
				.filter((w) => w.type === 'GLOBAL_EXPERIENCE')
				.find((w) => w.cloudId === contextCloudId),
		[workspaces, contextCloudId],
	);

	const workspaceActions = useWorkspaceActions();
	let workspaceDetails: WorkspaceDetails | undefined;
	if (workspace && orgId) {
		workspaceDetails = workspaceActions.initWorkspaceFromHomeFromVolt(workspace, {
			numWorkspaces: workspaceIds.length,
			userIsOrgAdmin: bootstrap.userIsOrgAdmin ?? false,
			organisationId: orgId,
		});
	}

	const userHasProjectAccess = workspace
		? checkUserHasCapabilitiesFromVolt(workspace, ['projects_app:access'], true)
		: false;
	const userHasGoalAccess = workspace
		? checkUserHasCapabilitiesFromVolt(workspace, ['goals_app:access'], true)
		: false;

	const shellData: BootstrapShellData = {
		orgId,
		cloudIds,
		orgCloudIds,
		workspaceIds,
		workspaceUuids,
		contextCloudId,
		collabContextSource: bootstrap.collabContext
			? {
					cloudIds: collabContextCloudIds,
					isMultiSite: bootstrap.collabContext.isMultiSite,
					hasMultipleUnits: bootstrap.collabContext.hasMultipleUnits,
					collabContextSiteAris,
				}
			: null,
		activeWorkspace:
			workspace && workspace.cloudId && workspace.uuid
				? { cloudId: workspace.cloudId, uuid: workspace.uuid }
				: undefined,
		userHasProjectAccess,
		userHasGoalAccess,
		userIsOrgAdmin: bootstrap.userIsOrgAdmin ?? false,
	};

	return (
		<BootstrapShell
			bootstrap={shellData}
			workspaceDetails={workspaceDetails}
			matchedRoute={props.matchedRoute}
			history={props.history}
		>
			{({ collabContext, currentUser }) =>
				props.children({
					orgId,
					cloudIds,
					workspaceIds,
					workspaceUuids,
					workspace: workspaceDetails,
					user: currentUser,
					workspaceVolt: workspace,
					collabContext,
				})
			}
		</BootstrapShell>
	);
}

/**
 * Server-inlined-JSON branch of the home bootstrap — reads the payload from
 * `globalThis.__TOWNSQUARE_BOOTSTRAP__`, calls the plain-data versions of
 * `initWorkspaceFromHome` and `checkUserHasCapabilities`, then hands the
 * flattened shape to `BootstrapShell` for everything else.
 *
 * WHAT STAYS HERE: anything that needs the inlined-payload data source. WHAT
 * GOES TO THE SHELL: all branch-agnostic side effects (org context, collab
 * context, statsig init, monitoring, route preload, app-state transition).
 *
 * Becomes the only branch once Volt is at 100% on home. Sibling:
 * `Bootstrap.tsx` (GQL).
 */
export function BootstrapFromVolt(props: BootstrapFromVoltProps): React.JSX.Element {
	// Must run before <Suspense>: BootstrapErrorBoundary reads it when the populator throws.
	userAccessRedirectContextHolder.initUserAccessRedirect(routes);

	if (props.matchedRoute?.route.name === '404-page') {
		return <NotFoundScreen />;
	}

	return (
		<BootstrapErrorBoundary>
			<Suspense interactionName="spa-loader" fallback={<FullScreenSpaLoader />}>
				<BootstrapFromVoltResolution {...props} />
			</Suspense>
		</BootstrapErrorBoundary>
	);
}
